import React, { useEffect, useState, useCallback, useMemo } from 'react';
import PropTypes from 'prop-types';
import cn from 'classnames';
import { useForm } from 'react-hook-form';
import { RevenueHero } from '@revenuehero/sdk-react';
import styles from './styles.module.scss';
import Heading from '../../shared/heading';
import Input from '../../shared/input';
import Button from '../../shared/button';
import SuccessMessage from '../SuccessMessage';
import { BUTTON_THEMES, BUTTON_SIZES } from 'constants/button';

const BookMeetingNew = ({
  title = null,
  description = null,
  revenueheroRouterId,
  blockId = null,
}) => {
  const [isMeetingScheduled, setIsMeetingScheduled] = useState(false);
  const {
    register,
    handleSubmit,
    formState: { errors },
  } = useForm();

  useEffect(() => {
    const handleMessage = (ev) => {
      if (ev.data.type === 'MEETING_BOOKED' || ev.data.type === 'MEETING_RESCHEDULED') {
        setIsMeetingScheduled(true);
        window?.scrollTo({ top: 0, behavior: 'smooth' });
      }
    };

    window.addEventListener('message', handleMessage);
    return () => window.removeEventListener('message', handleMessage);
  }, []);

  const onSubmit = useCallback(() => {
    // eslint-disable-next-line no-console
    // console.log('Form submitted:', data);
  }, []);

  const formSelector = useMemo(
    () => `#revenuehero-form-${revenueheroRouterId}`,
    [revenueheroRouterId]
  );

  if (!revenueheroRouterId) {
    return null;
  }

  if (isMeetingScheduled) {
    return (
      <div id={blockId} className="container">
        <section className={styles.root}>
          <div className={styles.embedContainer}>
            <SuccessMessage
              title="Meeting Scheduled!"
              description="Thank you for booking a meeting with us. We've sent a confirmation email with the details."
              buttonLabel="Back to Home"
              buttonLink="/"
            />
          </div>
        </section>
      </div>
    );
  }

  return (
    <div id={blockId} className="container">
      <section className={cn(styles.root)}>
        <div className={styles.header}>
          {title && (
            <Heading size="xl" className={styles.title}>
              {title}
            </Heading>
          )}
          {description && (
            <div className={styles.description} dangerouslySetInnerHTML={{ __html: description }} />
          )}
        </div>

        <div className={styles.embedContainer}>
          <form
            id={`revenuehero-form-${revenueheroRouterId}`}
            onSubmit={handleSubmit(onSubmit)}
            className={styles.form}
          >
            <div className={styles.formGrid}>
              <Input
                {...register('First Name', { required: 'First name is required' })}
                placeholder="First Name"
                error={errors.firstname?.message}
                withBorder
              />
              <Input
                {...register('Last Name', { required: 'Last name is required' })}
                placeholder="Last Name"
                error={errors.lastname?.message}
                withBorder
              />
              <Input
                {...register('Email', {
                  required: 'Email is required',
                  pattern: {
                    value: /^[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,}$/i,
                    message: 'Invalid email address',
                  },
                })}
                placeholder="Email address"
                type="email"
                error={errors.email?.message}
                withBorder
              />
              {/* phone */}
              <Input
                {...register('Phone', { required: 'Phone is required' })}
                placeholder="Phone"
                error={errors.phone?.message}
                withBorder
              />
            </div>

            <div className={styles.submitWrapper}>
              <Button
                type="submit"
                theme={BUTTON_THEMES.ACCENT_PRIMARY}
                size={BUTTON_SIZES.LG}
                className={styles.submitButton}
              >
                Book a meeting
              </Button>
            </div>
          </form>

          <RevenueHero enabled routerId={revenueheroRouterId} formId={formSelector} />
        </div>
      </section>
    </div>
  );
};

BookMeetingNew.propTypes = {
  title: PropTypes.string,
  description: PropTypes.string,
  revenueheroRouterId: PropTypes.number.isRequired,
  blockId: PropTypes.string,
};

export default BookMeetingNew;
